import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  concatMap,
  filter,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { Reply } from '@shared/reply/models';
import { ReplyApiService, ReplyService } from '../../../../services';
import { AddEditReplyComponent } from '../add-edit-reply';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';

@Component({
  selector: 'tablesready-replies-list',
  templateUrl: './replies.component.html',
  styleUrls: ['./replies.component.scss'],
})
export class RepliesComponent implements OnInit, OnDestroy {
  @Input() customerPluralLabel: string;
  @Input() isMobile = false;
  @Input() isOnBusinessPlan = false;
  @Output() freePlanDetected = new EventEmitter<void>();
  replies$: Observable<Reply[]>;
  icon = AppIcon;
  iconColor = AppIconColor;
  repliesCount = 0;
  private readonly subscriptions = new Subscription();
  private readonly repliesSubject = new BehaviorSubject<Reply[]>([]);

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly replyService: ReplyService,
    private readonly replyApiService: ReplyApiService,
    private readonly alertsService: AlertsService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {
    this.replies$ = this.repliesSubject.asObservable();
  }

  ngOnInit(): void {
    this.subscriptions.add(this.replyService.listenToChanges().subscribe());

    const repliesSubscription = this.replyService.load()
      .pipe(
        switchMap(() => this.replyService.selectAll()),
        tap(replies => {
          this.repliesCount = replies.length;
          this.repliesSubject.next(replies);
          this.cdr.detectChanges();
        }),
      );
    this.subscriptions.add(repliesSubscription.subscribe());

    // Close add modal if limit is reached
    const closeAddModalSubscription = this.replies$.pipe(
      filter(replies => replies.length >= this.appVariables.MAX_SAVED_REPLIES),
      concatMap(() => this.modalController.dismissAsync()),
      tap((hasBeenClosed) => {
        if (!hasBeenClosed) {
          return;
        }

        this.alertsService.addInfo('Replies limit has been reached.');
      }),
    )
      .subscribe();
    this.subscriptions.add(closeAddModalSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.replyService.reset();
  }

  addReply(): void {
    if (this.repliesCount === this.appVariables.MAX_SAVED_REPLIES) {
      return;
    }

    if (!this.isOnBusinessPlan) {
      this.freePlanDetected.emit();

      return;
    }

    this.modalController.create(AddEditReplyComponent, {reply$: of(null)})
      .pipe(take(1))
      .subscribe();
  }

  editReply(id: string) {
    this.modalController.create(AddEditReplyComponent, {
      reply$: this.replies$
        .pipe(map(replies => (replies.length ? replies.find(reply => reply.id === id) || null : null))),
    })
      .pipe(take(1))
      .subscribe();
  }

  deleteReply(id: string) {
    this.replyApiService.delete(id)
      .pipe(
        tap(() => this.alertsService.addSuccess('Reply has been successfully deleted.', '')),
        catchError(error => {
          const errorMessage = error.error?.message || 'Error deleting reply.';
          this.alertsService.addError(errorMessage);

          return of(null);
        }),
      )
      .subscribe();
  }

  trackById(reply: Reply): string {
    return reply.id;
  }

  get remainingReplies(): number {
    if (!this.isOnBusinessPlan) {
      return this.appVariables.MAX_SAVED_REPLIES;
    }

    return this.appVariables.MAX_SAVED_REPLIES - this.repliesCount;
  }
}
